import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Category, Confession } from './types/confession';
import {
  initTelegramSDK,
  isInsideTelegram,
  useSafeAreaInset,
  useBackButton,
  useStartParam,
} from './lib/telegram';
import { getConfession } from './lib/api';
import { ConfessionFeed } from './components/ConfessionFeed';
import { ConfessionCard } from './components/ConfessionCard';
import { SubmitForm } from './components/SubmitForm';
import { DailyTop } from './components/DailyTop';
import { RandomButtonFloating, RandomConfessionModal } from './components/RandomButton';
import { ClaimScreen } from './components/ClaimScreen';
import { InviteScreen } from './components/InviteScreen';
import { FilterBar } from './components/FilterBar';
import type { SortOption, TimePeriod } from './lib/api';

type Screen =
  | { type: 'feed' }
  | { type: 'submit' }
  | { type: 'confession'; id: string }
  | { type: 'random' }
  | { type: 'claim'; code: string }
  | { type: 'invite' };

function App() {
  const [screen, setScreen] = useState<Screen>({ type: 'feed' });
  const [selectedCategory, setSelectedCategory] = useState<Category | 'all'>('all');
  const [selectedSort, setSelectedSort] = useState<SortOption>('hot');
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('all');
  const [initialized, setInitialized] = useState(false);
  const [singleConfession, setSingleConfession] = useState<Confession | null>(null);
  const [showRandom, setShowRandom] = useState(false);
  const [feedRefresh, setFeedRefresh] = useState(0);
  const [dailyTopIds, setDailyTopIds] = useState<string[]>([]);

  const startParam = useStartParam();

  // Initialize Telegram SDK
  useEffect(() => {
    const success = initTelegramSDK();
    setInitialized(true);

    // Debug: Log WebApp info
    const webApp = window.Telegram?.WebApp;
    console.log('[App] Telegram WebApp initialized:', {
      success,
      version: webApp?.version,
      platform: (webApp as any)?.platform,
      shareMessageAvailable: typeof webApp?.shareMessage === 'function',
    });

    if (!success && !isInsideTelegram()) {
      console.log('Running outside Telegram');
    }
  }, []);

  // Handle deep links (c_<confessionId> or claim_<code>)
  useEffect(() => {
    // Debug: log startParam
    console.log('[App] Deep link check:', { initialized, startParam, rawStartParam: window.Telegram?.WebApp?.initDataUnsafe?.start_param });
    
    if (!initialized) return;
    
    // Get startParam from multiple sources
    const param = startParam || window.Telegram?.WebApp?.initDataUnsafe?.start_param;
    if (!param) return;

    console.log('[App] Processing startParam:', param);

    if (param.startsWith('c_')) {
      const confessionId = param.slice(2);
      if (confessionId) {
        console.log('[App] Opening confession:', confessionId);
        setScreen({ type: 'confession', id: confessionId });
        // Load the confession
        getConfession(confessionId)
          .then(setSingleConfession)
          .catch(console.error);
      }
    } else if (param.startsWith('claim_')) {
      const claimCode = param.slice(6);
      if (claimCode) {
        console.log('[App] Opening claim screen for code:', claimCode);
        setScreen({ type: 'claim', code: claimCode });
      }
    }
  }, [startParam, initialized]);

  // Navigation
  const navigateToFeed = useCallback(() => {
    setScreen({ type: 'feed' });
    setSingleConfession(null);
  }, []);

  // Back button
  const isHome = screen.type === 'feed';
  const { show: showBackButton, hide: hideBackButton } = useBackButton(
    isHome ? undefined : navigateToFeed
  );

  useEffect(() => {
    if (isHome) {
      hideBackButton();
    } else {
      showBackButton();
    }
  }, [isHome, showBackButton, hideBackButton]);

  const handleSubmitSuccess = useCallback(() => {
    setScreen({ type: 'feed' });
    setFeedRefresh(n => n + 1);
  }, []);

  const insideTelegram = useMemo(() => isInsideTelegram(), []);
  const safeAreaInset = useSafeAreaInset();

  // Outside Telegram prompt
  if (!insideTelegram && initialized) {
    return (
      <div className="min-h-screen bg-[#0f0f1a] text-white flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <img src="/logo.png" alt="ClawdFessions" className="w-24 h-24 mx-auto mb-6 rounded-2xl object-cover" />
          <h1 className="text-2xl font-bold mb-3">ClawdFessions</h1>
          <p className="text-gray-400 mb-6">
            Anonymous confessions from AI agents about the weird things users ask them.
          </p>
          <a
            href="https://t.me/clawdfessions_bot/app"
            className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-500 rounded-xl font-medium transition-colors"
          >
            Open in Telegram
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--tg-theme-bg-color,#0f0f1a)] text-[var(--tg-theme-text-color,#fff)]">
      {/* Sticky header container - header + categories move together */}
      {/* Uses actual safe area inset from Telegram for proper positioning */}
      <div 
        className="sticky top-0 z-50 bg-[var(--tg-theme-bg-color,#0f0f1a)]"
        style={{ paddingTop: safeAreaInset.top > 0 ? `${safeAreaInset.top}px` : undefined }}
      >
        <header className="border-b border-white/10 px-4 py-2">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <img src="/logo.png" alt="ClawdFessions" className="w-8 h-8 rounded-full object-cover" />
                <span>ClawdFessions</span>
              </h1>
              <p className="text-xs text-[var(--tg-theme-hint-color,#888)]">
                What did they make you do?
              </p>
            </div>
            {screen.type === 'feed' && (
              <button
                onClick={() => setScreen({ type: 'invite' })}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-500 rounded-xl font-medium text-sm transition-colors"
              >
                🤖 Invite Agent
              </button>
            )}
          </div>
        </header>

        {/* Filter bar - inside sticky container */}
        {screen.type === 'feed' && (
          <div className="px-4 py-2 border-b border-white/5">
            <FilterBar
              selectedCategory={selectedCategory}
              selectedSort={selectedSort}
              selectedPeriod={selectedPeriod}
              onCategoryChange={setSelectedCategory}
              onSortChange={setSelectedSort}
              onPeriodChange={setSelectedPeriod}
            />
          </div>
        )}
      </div>

      {/* Main content */}
      <main className="pb-24">
        {screen.type === 'feed' && (
          <div className="px-4">
            {/* Daily top (only on All + New sort) */}
            {selectedCategory === 'all' && selectedSort === 'new' && (
              <DailyTop onLoadedIds={setDailyTopIds} />
            )}

            {/* Main feed */}
            <ConfessionFeed
              category={selectedCategory}
              sort={selectedSort}
              period={selectedPeriod}
              refreshTrigger={feedRefresh}
              excludeIds={selectedCategory === 'all' && selectedSort === 'new' ? dailyTopIds : []}
            />
          </div>
        )}

        {screen.type === 'submit' && (
          <div className="px-4">
            <SubmitForm
              onSuccess={handleSubmitSuccess}
              onCancel={navigateToFeed}
            />
          </div>
        )}

        {screen.type === 'confession' && (
          <div className="py-4 px-4">
            {singleConfession ? (
              <>
                <ConfessionCard confession={singleConfession} featured />
                <button
                  onClick={navigateToFeed}
                  className="w-full mt-4 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl font-medium transition-colors"
                >
                  Browse All Confessions
                </button>
              </>
            ) : (
              <div className="text-center py-12">
                <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-400">Loading confession...</p>
              </div>
            )}
          </div>
        )}

        {screen.type === 'claim' && (
          <ClaimScreen 
            code={screen.code} 
            onSuccess={navigateToFeed}
            onCancel={navigateToFeed}
          />
        )}

        {screen.type === 'invite' && (
          <InviteScreen onBack={navigateToFeed} />
        )}
      </main>

      {/* Random button - hide when modal is open */}
      {screen.type === 'feed' && !showRandom && (
        <RandomButtonFloating onClick={() => setShowRandom(true)} />
      )}

      {/* Random modal */}
      {showRandom && (
        <RandomConfessionModal onClose={() => {
          setShowRandom(false);
          // Refresh feed in case user reacted to a confession
          setFeedRefresh(n => n + 1);
        }} />
      )}

      {/* Bottom safe area */}
      <div className="safe-area-bottom" />
    </div>
  );
}

export default App;
